<template>
  <v-dialog
    v-model="isOpen"
    max-width="800px"
    @click:outside="closeModal">
    <v-card>
      <v-card-title>
        <span class="text-h5">Import Workflow Timelines</span>
      </v-card-title>

      <v-card-text>
        <v-file-input
          :key="componentKey"
          v-model="selectedFile"
          label="Choose CSV file"
          accept=".csv"
          dense
          outlined
          hide-details
          @change="handlePreview"
        />
      </v-card-text>

      <v-alert
        v-if="message"
        :type="messageType"
        dense
        text
        class="mx-4"
      >
        {{ message }}
      </v-alert>

      <v-data-table
        v-if="previewData.length > 0"
        :headers="tableHeaders"
        :items="previewData"
        :hide-default-footer="true"
        :items-per-page="-1"
        fixed-header
        class="elevation-1 mx-4 mb-4"
        style="max-height: 300px; overflow-y: auto;"
      >
        <template v-slot:item.action="{ item }">
          <v-chip
            x-small
            :color="item.action === 'added' ? 'success' : 'primary'"
            dark
          >
            {{ item.action === 'added' ? 'เพิ่มใหม่' : 'อัปเดต' }}
          </v-chip>
        </template>
        <template v-slot:item.errors="{ item }">
          <span
            v-if="item.errors.length"
            class="error--text caption"
          >
            {{ item.errors.join(', ') }}
          </span>
          <v-icon
            v-else
            small
            color="success"
          >
            mdi-check
          </v-icon>
        </template>
      </v-data-table>

      <v-card-text class="d-flex justify-end py-0">
        <v-btn
          text
          small
          color="primary"
          class="text-decoration-underline"
          @click="downloadTemplate"
        >
          Download Template CSV
        </v-btn>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          color="error"
          @click="closeModal"
        >
          Cancel
        </v-btn>
        <v-btn
          text
          color="primary"
          :disabled="!canImport"
          :loading="loading"
          @click="handleImport"
        >
          Import
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as XLSX from 'xlsx'

export default {
  props: {
    activeStages: {
      type: Array,
      default: () => []
    },
    jobs: {
      type: Array,
      default: () => []
    },
    existingTimelines: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isOpen: true,
      loading: false,
      selectedFile: null,
      previewData: [],
      componentKey: 0,
      message: '',
      messageType: 'info',
      tableHeaders: [
        { text: 'สถานะ', value: 'action', sortable: false, width: '110px' },
        { text: 'Timeline', value: 'timelineName', sortable: false },
        { text: 'Stage Errors', value: 'errors', sortable: false }
      ]
    }
  },
  computed: {
    canImport () {
      return !this.loading
        && this.previewData.length > 0
        && this.previewData.every((row) => row.errors.length === 0)
        && this.messageType !== 'error'
    }
  },
  methods: {
    downloadTemplate () {
      const headers = ['Timeline']
      this.activeStages.forEach((stage) => {
        const job = this.jobs.find((j) => j.value === stage.pic)
        const jobLabel = job ? job.name : stage.pic || ''
        headers.push(`${stage.name} (${jobLabel}) Start`)
        headers.push(`${stage.name} (${jobLabel}) Due`)
      })
      headers.push('Launch')

      const ws = XLSX.utils.aoa_to_sheet([headers])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Workflow')
      XLSX.writeFile(wb, 'workflow-template.csv')
    },
    buildColumnMap () {
      const columnMap = {}
      this.activeStages.forEach((stage) => {
        const job = this.jobs.find((j) => j.value === stage.pic)
        const jobLabel = job ? job.name : stage.pic || ''
        const stageLabel = `${stage.name} (${jobLabel})`
        columnMap[`${stageLabel} Start`.toLowerCase()] = { stageId: stage.id, field: 'start', label: stage.name }
        columnMap[`${stageLabel} Due`.toLowerCase()] = { stageId: stage.id, field: 'due', label: stage.name }
      })
      return columnMap
    },
    validateStageColumns (fields, columnMap) {
      const fieldsLower = fields.map((f) => f.toLowerCase())
      const missingItems = new Set()

      Object.entries(columnMap).forEach(([key, mapping]) => {
        if (!fieldsLower.includes(key)) missingItems.add(mapping.label)
      })

      if (!fields.includes('Launch')) missingItems.add('Launch')

      if (missingItems.size === 0) return ''

      return `ไม่พบ stage ในไฟล์: ${Array.from(missingItems).join(', ')}`
    },
    validateRow (row, fields, columnMap) {
      const errors = []
      const stageValues = {}
      const problemStageIds = new Set()

      const launchValue = row['Launch']
      if (launchValue === '' || launchValue === undefined || launchValue === null) {
        errors.push('กรุณาระบุ Launch')
      } else if (!/^[0-9]+$/.test(String(launchValue)) || Number(launchValue) === 0) {
        errors.push('Launch ต้องเป็นตัวเลขจำนวนเต็มบวก')
      }

      fields.forEach((field) => {
        if (field === 'Timeline' || field === 'Launch') return
        const mapping = columnMap[field.toLowerCase()]
        if (!mapping) return
        const value = row[field]
        if (value === '' || value === undefined || value === null) {
          problemStageIds.add(mapping.stageId)
          return
        }
        if (!/^[0-9]+$/.test(String(value)) || Number(value) === 0) {
          problemStageIds.add(mapping.stageId)
          return
        }
        if (!stageValues[mapping.stageId]) stageValues[mapping.stageId] = {}
        stageValues[mapping.stageId][mapping.field] = Number(value)
      })

      Object.entries(stageValues).forEach(([stageId, vals]) => {
        if (vals.start && vals.due && vals.due < vals.start) {
          problemStageIds.add(stageId)
        }
      })

      if (problemStageIds.size > 0) {
        const stageNames = Array.from(problemStageIds).map((stageId) => {
          const stage = this.activeStages.find((s) => s.id === stageId)
          return stage ? stage.name : stageId
        })
        errors.push(`${stageNames.join(', ')}`)
      }

      return errors
    },
    handlePreview () {
      this.message = ''
      this.messageType = 'info'
      this.previewData = []
      if (!this.selectedFile) return
      this.readFile(this.selectedFile)
    },
    readFile (file) {
      if (!(file instanceof Blob)) {
        this.message = 'Invalid file format.'
        this.messageType = 'error'
        return
      }
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'string' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false })
          this.processPreviewData(rows)
        } catch (err) {
          console.error(err)
          this.message = 'ไม่สามารถอ่านไฟล์ได้ กรุณาตรวจสอบ format'
          this.messageType = 'error'
        }
      }
      reader.readAsText(file)
    },
    processPreviewData (rows) {
      if (rows.length === 0) {
        this.message = 'ไม่พบข้อมูลใน CSV'
        this.messageType = 'error'
        return
      }

      const fields = Object.keys(rows[0])

      if (!fields.includes('Timeline')) {
        this.message = 'ไฟล์ไม่ถูกต้อง: ไม่พบ column "Timeline"'
        this.messageType = 'error'
        return
      }

      const columnMap = this.buildColumnMap()

      const stageColumnCheck = this.validateStageColumns(fields, columnMap)
      if (stageColumnCheck) {
        this.message = stageColumnCheck
        this.messageType = 'error'
        return
      }

      const nameSeen = new Set()
      const duplicateNames = new Set()
      rows.forEach((row) => {
        const name = (row['Timeline'] || '').trim().toLowerCase()
        if (!name) return
        if (nameSeen.has(name)) duplicateNames.add(name)
        else nameSeen.add(name)
      })

      this.previewData = rows
        .map((row) => {
          const timelineName = (row['Timeline'] || '').trim()
          if (!timelineName) return null
          const isExisting = this.existingTimelines.some(
            (t) => t.name.trim().toLowerCase() === timelineName.toLowerCase()
          )
          const errors = this.validateRow(row, fields, columnMap)
          if (duplicateNames.has(timelineName.toLowerCase())) {
            errors.push('Timeline ซ้ำกันในไฟล์')
          }
          return {
            timelineName,
            action: isExisting ? 'updated' : 'added',
            errors,
            rawRow: row,
            rawFields: fields
          }
        })
        .filter(Boolean)

      const hasErrors = this.previewData.some((r) => r.errors.length > 0)
      if (hasErrors) {
        this.message = 'บางแถวมีข้อมูลไม่ถูกต้อง กรุณาแก้ไขก่อน Import'
        this.messageType = 'error'
      }
    },
    handleImport () {
      if (!this.canImport) return
      const columnMap = this.buildColumnMap()

      const result = this.previewData.map((entry) => {
        const { timelineName, action, rawRow, rawFields } = entry
        const changes = []

        rawFields.forEach((field) => {
          if (field === 'Timeline' || field === 'Launch') return
          const mapping = columnMap[field.toLowerCase()]
          if (!mapping) return
          const value = rawRow[field]
          if (value === undefined || value === null) return
          changes.push({ stageId: mapping.stageId, field: mapping.field, value })
        })

        return { timelineName, action, changes, launch: rawRow['Launch'] }
      })

      this.$emit('imported', result)
      this.closeModal()
    },
    closeModal () {
      this.isOpen = false
      this.resetForm()
      this.$emit('close')
    },
    resetForm () {
      this.selectedFile = null
      this.message = ''
      this.messageType = 'info'
      this.previewData = []
      this.componentKey += 1
    }
  }
}
</script>

<style scoped>
.v-alert {
  margin: 0 auto;
}
</style>
